import React from 'react';
import { Instagram, Facebook, Linkedin } from 'lucide-react';
import Button from '../ui/Button';
import MarigoldLogo from '../ui/MarigoldLogo';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const socialLinks: { name: string; url: string; Icon: React.ComponentType<{ className?: string }> }[] = [
    { name: 'instagram', url: 'https://www.instagram.com/marigoldone11/', Icon: Instagram },
    { name: 'facebook', url: 'https://www.facebook.com/profile.php?id=100089046251414', Icon: Facebook },
    { name: 'linkedin', url: 'https://www.linkedin.com/company/marigold-one11', Icon: Linkedin }
  ];
  const serviceLinks = ['AI Integration', 'Digital Marketing', 'Website Development', 'Business Automation', 'Strategic Consulting', 'AI Governance'];
  const productLinks = ['dAisy Ad Management', 'ONE11 Suite', 'ONE11 Analytics', 'AI Policy Framework', 'Training Programs', 'Case Studies'];
  
  // Function to format dAisy text with proper colors
  const formatDaisyText = (text: string) => {
    if (text.includes('dAisy')) {
      return (
        <>
          <span className="text-[#2a8735]">D</span>
          <span className="text-[#f59d40]">AI</span>
          <span className="text-[#2a8735]">sy</span>
          {text.replace('dAisy', '')}
        </>
      );
    }
    return text;
  };
  
  return (
    <footer className="bg-white text-[#2a2b2a] py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="mb-4">
              <MarigoldLogo size="md" />
            </div>
            <div className="text-gray-600 mb-6 leading-relaxed">
              <p className="mb-3">
                AI-driven business solutions that save you time at every step. We use AI for everything so like you, we can deliver results faster and unplug sooner.
              </p>
              <div className="text-sm pt-3 border-t border-orange-200/50">
                <span className="text-gray-500">Discover how AI transformed our agency.</span>
                <Link 
                  to="/our-ai-story" 
                  className="text-[#f59d40] hover:text-[#ea5830] ml-1 font-medium transition-colors duration-300"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Read the whole story →
                </Link>
              </div>
            </div>
            <div className="flex space-x-3">
              {socialLinks.map(({ name, url, Icon }) => (
                <a key={name} href={url} className="group" target="_blank" rel="noopener noreferrer">
                  <span className="sr-only">{name}</span>
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-orange-100 to-red-100 border border-orange-200 flex items-center justify-center group-hover:from-[#f59d40] group-hover:to-[#ea5830] group-hover:border-[#f59d40] transition-all duration-300 shadow-sm">
                    <Icon className="h-5 w-5 text-gray-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                </a>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-bold mb-4">Services</h3>
            <ul className="space-y-2">
              {serviceLinks.map((item) => (
                <li key={item}>
                  <Link 
                    to={`/services/${item.toLowerCase().replace(/\s+/g, '-')}`} 
                    className="text-gray-600 hover:text-[#f59d40] transition-colors"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-bold mb-4">Products</h3>
            <ul className="space-y-2">
              {productLinks.map((item) => (
                <li key={item}>
                  <Link 
                    to={item === 'dAisy Ad Management' ? '/daisy-ad-management' : `/products/${item.toLowerCase().replace(/\s+/g, '-')}`} 
                    className="text-gray-600 hover:text-[#f59d40] transition-colors"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    {formatDaisyText(item)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <address className="not-italic text-gray-600 space-y-2 mb-4">
              <p>17117 Oak Dr Suite G</p>
              <p>Omaha, NE 68130</p>
              <p>hello@marigold111.com</p>
            </address>
            <Button variant="primary" size="sm" href="/contact">Schedule a Call</Button>
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} MarigoldONE11. All rights reserved.</p>
          <div className="mt-2 space-x-3">
            <Link to="/privacy" className="hover:text-[#f59d40] underline">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-[#f59d40] underline">Terms of Service</Link>
            <span>•</span>
            <Link to="/cookie-policy" className="hover:text-[#f59d40] underline">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 